<template>
  <a-modal title="Update unpaid shift rates" width="900px" :visible="visible" @cancel="closeModal">
    <div class="flex flex-column gap-2">
      <div class="grid">
        <span class="col-12"
          ><a-alert
            message="Choose a rate and period to preview changes before applying them to unpaid shifts."
            type="info"
            show-icon
        /></span>

        <b class="col-4">Payment Type</b>
        <span class="col-8">{{ paymentInfo.type }}</span>
        <b class="col-4">Payment Status</b>
        <span class="col-8">{{ PAYMENT_WORK_STATUS[paymentInfo.status] }}</span>
        <b class="col-4">Payment Method</b>
        <span class="col-8">{{ PAYMENT_METHOD[paymentInfo.method] }}</span>

        <template v-if="paymentInfo.legal_entity">
          <b class="col-4">Legal Entity</b>
          <span class="col-8">{{ paymentInfo.legal_entity.name }}</span>
        </template>

        <b class="col-4">All unpaid shifts</b>
        <a-checkbox class="col-8" v-model="form.is_all_shifts" />

        <template v-if="!form.is_all_shifts">
          <b class="col-4">Dates</b>
          <span class="col-8">
            <a-range-picker class="w-full" format="MM/DD/YYYY" v-model="dateProxy" />
            <div v-if="periodError" class="period-error">{{ periodError }}</div>
          </span>
        </template>

        <b class="col-4">Rate Type</b>
        <span class="col-8">
          <a-radio-group v-model="form.rate_type" button-style="solid">
            <a-radio-button value="hourly_rate">Hourly</a-radio-button>
            <a-radio-button value="training_rate">Training</a-radio-button>
            <a-radio-button value="orientation_rate">Orientation</a-radio-button>
          </a-radio-group>
        </span>

        <b class="col-4">Rate</b>
        <span class="col-8">
          <custom-select
            class="w-full"
            :multiple="false"
            :value.sync="form.rate"
            placeholder="Rate"
            :options="getRates(form.rate_type)"
            :loading="loadingHistory"
            name="title"
            optionKey="id"
          />
          <div v-if="rateError" class="period-error">{{ rateError }}</div>
        </span>
      </div>

      <div class="support-block" v-if="showSupport">
        <span>
          To change the Payment Type or Legal Entity, update it in your profile.
          <a-button class="h-fit p-0" type="link" @click="openProfileEditor">Edit Profile</a-button>
        </span>
        <span>
          To create a new rate, update it in your profile.
          <a-button class="h-fit p-0" type="link" @click="openRateModal">Create New Rate</a-button>
        </span>
      </div>
      <section v-if="preview" class="preview-results">
        <a-alert
          v-if="isApplied"
          message="Rate update result"
          :description="resultDescription"
          type="success"
          show-icon
        />
        <a-alert
          v-else-if="preview.counts?.changes === 0"
          message="No rate changes were found for this selection."
          type="info"
          show-icon
        />

        <a-tabs>
          <a-tab-pane key="changed" :tab="`Changed (${preview.counts?.changes ?? 0})`">
            <a-table
              size="small"
              row-key="payment_info_id"
              :columns="changeColumns"
              :data-source="preview.changes ?? []"
              :pagination="{ pageSize: 50 }"
              :scroll="{ y: 300 }"
            />
          </a-tab-pane>
          <a-tab-pane key="unchanged" :tab="`Unchanged (${preview.counts?.unchanged ?? 0})`">
            <a-table
              size="small"
              row-key="payment_info_id"
              :columns="rateColumns"
              :data-source="preview.unchanged ?? []"
              :pagination="{ pageSize: 50 }"
              :scroll="{ y: 300 }"
            />
          </a-tab-pane>
          <a-tab-pane key="excluded" :tab="`Excluded (${preview.counts?.excluded ?? 0})`">
            <a-table
              size="small"
              row-key="payment_info_id"
              :columns="excludedColumns"
              :data-source="preview.excluded ?? []"
              :pagination="{ pageSize: 50 }"
              :scroll="{ y: 300 }"
            />
          </a-tab-pane>
        </a-tabs>
      </section>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <a-button
          v-if="!preview"
          type="primary"
          :disabled="!canPreview"
          :loading="loadingPreview"
          @click="requestPreview"
          >Preview</a-button
        >
        <a-button
          v-else-if="!isApplied"
          type="primary"
          :disabled="preview.counts?.changes === 0"
          :loading="loadingApply"
          @click="applyPreview"
          >Apply</a-button
        >
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Alert, Button, Checkbox, DatePicker, message, notification, Radio, Table, Tabs } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import CustomSelect from '@/components/ui/custom-select.vue'
import { PAYMENT_METHOD, PAYMENT_WORK_STATUS } from '@/store/modules/payment/constants'
import { UNPAID_SHIFT_RATE_EXCLUSION_REASON } from '@/store/modules/shifts/constants'
import { deepCopy } from '@/utils/deep-copy'
import moment from 'moment-timezone'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: shiftActions } = createNamespacedHelpers('shifts')
const formatRate = value => (typeof value === 'number' ? `$${(value / 100).toFixed(2)}` : ' - ')
const formatScheduledStart = value => moment.parseZone(value).format('MM/DD/YYYY h:mm A z')

const initialForm = {
  start: undefined,
  end: undefined,
  rate_type: 'hourly_rate',
  rate: 0,
  is_all_shifts: false
}

export default {
  components: {
    'a-range-picker': DatePicker.RangePicker,
    'a-button': Button,
    'a-alert': Alert,
    'a-checkbox': Checkbox,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-table': Table,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    'custom-select': CustomSelect
  },
  props: {
    visible: { type: Boolean, default: false },
    showSupport: { type: Boolean, default: true },
    initialStartDate: { type: [String, Object], default: undefined },
    initialEndDate: { type: [String, Object], default: undefined },
    initialRateType: { type: String, default: undefined }
  },
  data() {
    return {
      loadingPreview: false,
      loadingApply: false,
      loadingHistory: false,
      periodError: undefined,
      rateError: undefined,
      preview: undefined,
      isApplied: false,
      previewRefreshCount: 0,
      form: deepCopy(initialForm),
      rates: [],
      PAYMENT_METHOD,
      PAYMENT_WORK_STATUS,
      changeColumns: [
        { title: 'Scheduled Start', dataIndex: 'scheduled_start', customRender: formatScheduledStart },
        { title: 'Previous Rate', dataIndex: 'old_rate', customRender: formatRate },
        { title: 'New Rate', dataIndex: 'new_rate', customRender: formatRate }
      ],
      rateColumns: [
        { title: 'Scheduled Start', dataIndex: 'scheduled_start', customRender: formatScheduledStart },
        { title: 'Rate', dataIndex: 'rate', customRender: formatRate }
      ],
      excludedColumns: [
        { title: 'Scheduled Start', dataIndex: 'scheduled_start', customRender: formatScheduledStart },
        { title: 'Rate', dataIndex: 'rate', customRender: formatRate },
        {
          title: 'Reason',
          dataIndex: 'reason',
          customRender: reason => UNPAID_SHIFT_RATE_EXCLUSION_REASON[reason]?.title ?? 'Not eligible for update'
        }
      ]
    }
  },
  computed: {
    ...profileState({ associate: state => state.associate }),
    paymentInfo() {
      return this.associate?.payment_info ?? {}
    },
    projectId() {
      return this.associate?.project_id ?? this.$store.state.applicationState.currentProject?.id
    },
    canPreview() {
      return Boolean(
        this.associate?.id &&
        this.projectId &&
        Number.isInteger(this.form.rate) &&
        this.form.rate >= 0 &&
        (this.form.is_all_shifts || (this.form.start && this.form.end))
      )
    },
    resultDescription() {
      return `Applied: ${this.preview.applied_count ?? 0}. Already applied: ${this.preview.already_applied_count ?? 0}.`
    },
    dateProxy: {
      get() {
        return [this.form.start, this.form.end]
      },
      set(value) {
        this.form.start = value[0]
        this.form.end = value[1]
        this.periodError = undefined
        this.clearPreview()
      }
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        if (newVal) {
          this.resetForm()
          await this.loadData()
          const rateType = this.initialRateType ?? initialForm.rate_type
          this.form.start = this.normalizeInitialDate(this.initialStartDate)
          this.form.end = this.normalizeInitialDate(this.initialEndDate)
          this.form.rate_type = rateType
          this.form.rate = this.initialRateType
            ? (this.getRates(rateType)[0]?.id ?? this.paymentInfo[rateType] ?? 0)
            : (this.paymentInfo.hourly_rate ?? 0)
        } else {
          this.resetForm()
        }
      },
      immediate: true
    },
    'form.rate_type': {
      handler(newVal) {
        this.form.rate = this.getRates(newVal)[0]?.id ?? this.paymentInfo[newVal] ?? 0
        this.clearPreview()
      }
    },
    'form.rate': {
      handler() {
        this.rateError = undefined
        this.clearPreview()
      }
    },
    'form.is_all_shifts': {
      handler() {
        this.clearPreview()
      }
    }
  },
  methods: {
    ...profileActions(['getRateHistory']),
    ...shiftActions(['previewUnpaidShiftRates', 'applyUnpaidShiftRates']),
    showNotification(type, message, description) {
      notification[type]({ message, description })
    },
    resetForm() {
      this.form = deepCopy(initialForm)
      this.periodError = undefined
      this.rateError = undefined
      this.preview = undefined
      this.isApplied = false
      this.previewRefreshCount = 0
    },
    clearPreview() {
      if (!this.preview) return

      this.preview = undefined
      this.isApplied = false
      this.previewRefreshCount = 0
    },
    openProfileEditor() {
      this.closeModal()
      this.$emit('updateProfile')
    },
    openRateModal() {
      this.$emit('updateRate')
      message.info(
        'You no longer need to manually update your payment information—adding a new rate for a past date will automatically take care of it.',
        5
      )
      this.closeModal()
    },
    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    getRates(key) {
      const seenIds = new Set()
      return this.rates
        .map(item => ({ id: item[key], title: `$${(item[key] / 100).toFixed(2)}` }))
        .filter(rate => {
          if (rate.id === undefined || rate.id === null || seenIds.has(rate.id)) return false
          seenIds.add(rate.id)
          return true
        })
    },
    normalizeInitialDate(value) {
      if (!value) return undefined
      const date = moment(value)
      return date.isValid() ? date : undefined
    },
    getSelectionPayload() {
      const period = this.form.is_all_shifts
        ? { all_unpaid: true }
        : {
            start_date: moment(this.form.start).format('YYYY-MM-DD'),
            end_date: moment(this.form.end).format('YYYY-MM-DD')
          }
      return {
        project_id: this.projectId,
        associate_id: this.associate.id,
        rate_type: this.form.rate_type,
        rate: this.form.rate,
        period
      }
    },
    getErrorMessage(error) {
      const responseData = error?.data
      const fieldError = responseData?.preview_token ?? responseData?.rate ?? responseData?.period
      if (Array.isArray(fieldError)) return fieldError.join(' ')
      return fieldError ?? responseData?.detail ?? responseData?.msg ?? error?.message ?? 'An error occurred.'
    },
    async requestPreview() {
      if (!this.canPreview) {
        this.periodError = 'Select a complete date range or Actualize All Shifts.'
        return
      }
      this.loadingPreview = true
      this.periodError = undefined
      this.rateError = undefined
      try {
        this.preview = await this.previewUnpaidShiftRates(this.getSelectionPayload())
        this.isApplied = false
        this.previewRefreshCount = 0
      } catch (error) {
        if (error.status === 400) {
          if (error.data?.rate) {
            this.rateError = this.getErrorMessage(error)
          } else {
            this.periodError = this.getErrorMessage(error)
          }
        }
        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.loadingPreview = false
      }
    },
    async applyPreview() {
      this.loadingApply = true
      try {
        this.preview = await this.applyUnpaidShiftRates({
          ...this.getSelectionPayload(),
          preview_token: this.preview.preview_token
        })
        this.isApplied = true
        this.showNotification('success', 'Success', 'Unpaid shift rates have been updated.')
      } catch (error) {
        if (error.status === 409 && error.data) {
          this.previewRefreshCount += 1
          if (this.previewRefreshCount === 1) {
            this.preview = error.data
            this.isApplied = false
            this.showNotification(
              'warning',
              'Preview updated',
              'The data changed. Review the updated preview before applying.'
            )
          } else {
            this.preview = undefined
            this.isApplied = false
            this.showNotification('error', 'Conflict', 'The data changed again. Create a new preview before applying.')
          }
          return
        }
        this.preview = undefined
        this.isApplied = false
        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.loadingApply = false
      }
    },
    async loadData() {
      if (!this.associate?.id) return
      this.loadingHistory = true
      try {
        this.rates = await this.getRateHistory({ associate_id: this.associate.id })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingHistory = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 600px !important;
  overflow: auto;
}
::v-deep .ant-tabs-bar {
  margin: 0;
}

.support-block {
  display: flex;
  flex-direction: column;
}
.period-error {
  color: var(--error-color);
  margin-top: 4px;
}
.preview-results {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
